import { AddManagerModal } from 'components/modal/classroom/AddManagerModal';
import { Button, Divider, Modal, Space, Table, Tag, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { IClassroom } from 'interfaces/classrooms';
import { TextField } from '@refinedev/antd';
import { formatDate } from 'services/date';
import { useCallback, useState } from 'react';
import { useCan, useCreate, useDelete, useList, useModal } from '@refinedev/core';
import type { IAgent } from 'interfaces/agents';

export interface ClassroomManagersProps {
  classroom: IClassroom;
}

const ClassroomManagers = ({ classroom }: ClassroomManagersProps) => {
  const resource = `classrooms/${classroom.id}/managers`;
  const addAgentModal = useModal();
  const deleteModal = useModal();
  const { data: canEdit } = useCan({ resource: 'classrooms', action: 'edit' });

  const [agentIdForDelete, setAgentIdForDelete] = useState('');

  const { data, isLoading: isListLoading, refetch } = useList<IAgent>({
    resource,
    pagination: {
      mode: 'server',
      current: 1,
      pageSize: 9999
    }
  });

  const managers = data?.data || [];
  const totalManagers = data?.total || managers.length;
  const { mutateAsync: createManager, isLoading: isCreatingManager } = useCreate();
  const { mutateAsync: deleteManager, isLoading: isDeletingManager } = useDelete();
  const isLoading = isCreatingManager || isDeletingManager;

  const onAddAgent = useCallback(
    async (agentId: string) => {
      await createManager({
        resource,
        values: {
          agent_id: agentId
        },
        successNotification: () => {
          addAgentModal.close();
          refetch();
          return {
            type: 'success',
            description: 'Sucesso!',
            message: 'Docente adicionado com sucesso'
          };
        },
        errorNotification: () => {
          return {
            type: 'error',
            description: 'Ops! Algo de errado',
            message: 'Não é possível adicionar o docente. Tente novamente!'
          };
        }
      });
    },
    [addAgentModal, createManager, refetch, resource]
  );

  const onDeleteAgent = useCallback(async () => {
    await deleteManager({
      resource,
      id: agentIdForDelete,
      successNotification: () => {
        deleteModal.close();
        refetch();
        return {
          type: 'success',
          description: 'Sucesso!',
          message: 'Docente removido com sucesso'
        };
      },
      errorNotification: () => {
        return {
          type: 'error',
          description: 'Ops! Algo de errado',
          message: 'Não foi possível remover o docente. Tente novamente!'
        };
      }
    });
  }, [agentIdForDelete, deleteManager, deleteModal, refetch, resource]);

  return (
    <>
      <Typography.Title level={5}>Corpo docente</Typography.Title>
      <Typography.Paragraph>
        Equipe responsável por gerenciar as atividades e oferecer suporte aos
        alunos. Esses usuários têm acesso à <strong>área do professor</strong>{' '}
        da turma e podem realizar as seguintes tarefas:
        <ul className="list-disc">
          <li>Visualizar informações da turma</li>
          <li>Criar e corrigir avaliações e atividades</li>
          <li>Gerenciar tópicos e respostas no fórum</li>
          <li>
            Responder perguntas enviadas pelos alunos na seção de tira-dúvidas
          </li>
          <li>Agendar ou cancelar aulas interativas</li>
          <li>Adicionar materiais complementares no conteúdo da turma</li>
        </ul>
      </Typography.Paragraph>

      <Divider />

      <Space
        align="center"
        className="w-full justify-between align-center mb-6"
      >
        <Space>
          <Typography.Title level={5} className="mb-0">
            Docentes
          </Typography.Title>
          <Tag>{totalManagers}</Tag>
        </Space>

        {canEdit?.can && (
          <Button type="primary" onClick={() => addAgentModal.show()}>
            Adicionar docente
          </Button>
        )}
      </Space>

      <Table dataSource={managers} loading={isListLoading} pagination={false} rowKey="id">
        <Table.Column
          dataIndex="name"
          key="name"
          title="Nome"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="created_at"
          key="name"
          title="Data de inclusão"
          render={value => (
            <TextField value={formatDate(value, 'DD/MM/YYYY')} />
          )}
        />
        {canEdit?.can && (
          <Table.Column<{ id: string }>
            dataIndex="evaluation-actions"
            key="evaluation-actions"
            align="center"
            title="Ações"
            render={(_, record) => {
              return (
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setAgentIdForDelete(record.id);
                    deleteModal.show();
                  }}
                />
              );
            }}
          />
        )}
      </Table>

      {addAgentModal.visible && (
        <AddManagerModal
          modal={addAgentModal}
          onSubmit={onAddAgent}
          classroomId={classroom.id}
          loading={isLoading}
        />
      )}

      <Modal
        destroyOnClose
        open={deleteModal.visible}
        onCancel={deleteModal.close}
        confirmLoading={isLoading}
        onOk={onDeleteAgent}
        okType="primary"
        okText="Confirmar remoção"
        title="Remover docente da equipe da turma"
        width={400}
        okButtonProps={{
          danger: true
        }}
      >
        <Typography.Text>
          Ao confirmar, o docente perderá acesso à área do professor e não
          poderá mais realizar tarefas associadas a esta turma
        </Typography.Text>
      </Modal>
    </>
  );
};

export default ClassroomManagers;
